import moment from 'moment'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { FILTER_DEFAULT_SKIP, FILTER_DEFAULT_TAKE } from '@apps-common/config/filter'
import { UNIT_ORDER_DATE_DELETED, UNIT_ORDER_DEFAULT, UNIT_STATUS_ACTIVE, UNIT_STATUS_CANCELLED } from '@apps-common/config/unit-filter'
import { IManagementUnitSearchParamsModel } from '@apps-common/types/management/unit'

// const MANAGEMENT_UNITS_FILER_DEFAULT_START_DATE_STR = moment('1990-01-01T00:00:00').toISOString()
// const MANAGEMENT_UNITS_FILER_DEFAULT_END_DATE_STR = moment().add(1, 'day').toISOString()

export const getManagementUnitsFilterDefaultStartDateStr = () => moment('2000-01-01T00:00:00').toISOString()
export const getManagementUnitsFilterDefaultEndDateStr = () => moment().add(1, 'day').toISOString()

// export const MANAGEMENT_UNITS_FILTER_MIN_DATE = moment(MANAGEMENT_UNITS_FILER_DEFAULT_START_DATE_STR).toDate()
// export const MANAGEMENT_UNITS_FILTER_MAX_DATE = moment().set({ hour: 23, minute: 59, second: 59, millisecond: 999 }).toDate()

//  Status
//  MCentreId
//  CompanyId
//  TechCompanyId
//  Site
//  AccountNo
//  IMEI
//  SerialNo
//  IsActivated
//  MobileNo
//  IPAddress
//  SimNo
//  CancellationProcessed
//  StartDate
//  EndDate
//  Skip
//  Take
//  OrderBy

export const DEFAULT_MANAGEMENT_UNITS_FILTER_STORE_PROPS: IManagementUnitsFilterProps = {
  // open: false,
  status: UNIT_STATUS_ACTIVE.value as string,
  mCentreId: '',
  companyId: '',
  techCompanyId: '',
  networkType: '',
  site: '',
  accountNo: '',
  imei: '',
  serialNo: '',
  mobileNo: '',
  ipAddress: '',
  simNo: '',
  unprocessedCancellations: false,
  hardwareToBeInvoiced: false,
  pinnedUnits: false,
  startDate: null,
  endDate: null,
  hasNoActivation: false,
  skip: FILTER_DEFAULT_SKIP,
  take: FILTER_DEFAULT_TAKE,
  orderBy: UNIT_ORDER_DEFAULT.value.toString(),
  savedState: null
}

export interface IManagementUnitsFilterProps {
  // open: boolean
  status: string
  mCentreId: string
  companyId: string
  techCompanyId: string
  networkType: string
  site: string
  accountNo: string
  imei: string
  serialNo: string
  mobileNo: string
  ipAddress: string
  simNo: string
  unprocessedCancellations: boolean
  hardwareToBeInvoiced: boolean
  pinnedUnits: boolean
  startDate: string | null
  endDate: string | null
  hasNoActivation: boolean
  skip: number
  take: number
  orderBy: string
  savedState: IManagementUnitsFilterProps | null
}

export interface IManagementUnitsFilterState extends IManagementUnitsFilterProps {
  // setOpen: (open: boolean) => void
  setStatus: (value: string) => void
  setMCentreId: (value: string) => void
  setCompanyId: (value: string) => void
  setTechCompanyId: (value: string) => void
  setNetworkType: (value: string) => void
  setSite: (value: string) => void
  setAccountNo: (value: string) => void
  setImei: (value: string) => void
  setSerialNo: (value: string) => void
  setMobileNo: (value: string) => void
  setIpAddress: (value: string) => void
  setSimNo: (value: string) => void
  setUnprocessedCancellations: (value: boolean) => void
  setHardwareToBeInvoiced: (value: boolean) => void
  setPinnedUnits: (value: boolean) => void
  setStartDate: (value: string) => void
  setEndDate: (value: string) => void
  setSkip: (value: number) => void
  setTake: (value: number) => void
  setOrderBy: (orderBy: string) => void
  getSearchParams: () => IManagementUnitSearchParamsModel
  getFilteredByKeywords: () => string[]
  setTempState: (tempState: IManagementUnitsFilterProps) => void
  clear: () => void
}

export const useManagementUnitsFilterStore = create<IManagementUnitsFilterState, [['zustand/persist', unknown]]>(
  persist(
    (set, get) => ({
      ...DEFAULT_MANAGEMENT_UNITS_FILTER_STORE_PROPS,
      // setOpen: (open: boolean) => set(() => ({ open })),
      setStatus: (value: string) => {
        const { status } = get()

        if (status !== value) {
          if (status === UNIT_STATUS_CANCELLED.value) {
            set(() => ({ status: value, skip: FILTER_DEFAULT_SKIP, orderBy: UNIT_ORDER_DATE_DELETED.value as string }))
          } else {
            set(() => ({ status: value, skip: FILTER_DEFAULT_SKIP, orderBy: UNIT_ORDER_DEFAULT.value as string }))
          }
        }
      },
      setMCentreId: (value: string) => {
        const { mCentreId } = get()

        if (mCentreId !== value) {
          set(() => ({ mCentreId: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setCompanyId: (value: string) => {
        const { companyId } = get()

        if (companyId !== value) {
          set(() => ({ companyId: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setTechCompanyId: (value: string) => {
        const { techCompanyId } = get()

        if (techCompanyId !== value) {
          set(() => ({ techCompanyId: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setNetworkType: (value) => {
        const { networkType } = get()

        if (networkType !== value) {
          set(() => ({ networkType: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setSite: (value: string) => {
        const { site } = get()

        if (site !== value) {
          set(() => ({ site: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setAccountNo: (value: string) => {
        const { accountNo } = get()

        if (accountNo !== value) {
          set(() => ({ accountNo: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setImei: (value: string) => {
        const { imei } = get()

        if (imei !== value) {
          set(() => ({ imei: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setSerialNo: (value: string) => {
        const { serialNo } = get()

        if (serialNo !== value) {
          set(() => ({ serialNo: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setMobileNo: (value: string) => {
        const { mobileNo } = get()

        if (mobileNo !== value) {
          set(() => ({ mobileNo: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setIpAddress: (value: string) => {
        const { ipAddress } = get()

        if (ipAddress !== value) {
          set(() => ({ ipAddress: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setSimNo: (value: string) => {
        const { simNo } = get()

        if (simNo !== value) {
          set(() => ({ simNo: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setUnprocessedCancellations: (value: boolean) => {
        const { unprocessedCancellations } = get()

        if (unprocessedCancellations !== value) {
          set(() => ({ unprocessedCancellations: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setHardwareToBeInvoiced: (value: boolean) => {
        const { hardwareToBeInvoiced } = get()

        if (hardwareToBeInvoiced !== value) {
          set(() => ({ hardwareToBeInvoiced: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setPinnedUnits: (value: boolean) => {
        const { pinnedUnits } = get()

        if (pinnedUnits !== value) {
          set(() => ({ pinnedUnits: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setStartDate: (value: string) => {
        const { startDate } = get()

        if (startDate !== value) {
          set(() => ({ startDate: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setEndDate: (value: string) => {
        const { endDate } = get()

        if (endDate !== value) {
          set(() => ({ endDate: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setSkip: (value: number) => set(() => ({ skip: value })),
      setTake: (value: number) => set(() => ({ take: value })),
      setOrderBy: (value: string) => {
        const { orderBy } = get()

        if (orderBy !== value) {
          set(() => ({ orderBy: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      getSearchParams: () => {
        const {
          status,
          mCentreId,
          companyId,
          techCompanyId,
          networkType,
          site,
          accountNo,
          imei,
          serialNo,
          mobileNo,
          ipAddress,
          simNo,
          unprocessedCancellations,
          hardwareToBeInvoiced,
          pinnedUnits,
          startDate,
          endDate,
          hasNoActivation,
          orderBy,
          skip,
          take
        } = get()

        return {
          status,
          mCentreId,
          companyId,
          techCompanyId,
          networkType,
          site,
          accountNo,
          imei,
          serialNo,
          mobileNo,
          ipAddress,
          simNo,
          unprocessedCancellations,
          hardwareToBeInvoiced,
          pinnedUnits,
          startDate,
          endDate,
          hasNoActivation,
          orderBy,
          skip,
          take
        }
      },
      getFilteredByKeywords: () => {
        const {
          status,
          mCentreId,
          companyId,
          techCompanyId,
          networkType,
          site,
          accountNo,
          imei,
          serialNo,
          mobileNo,
          ipAddress,
          simNo,
          startDate,
          endDate
        } = get()

        const keywords = []

        if (status !== UNIT_STATUS_ACTIVE.value) {
          keywords.push('Status')
        }

        if (mCentreId !== '') {
          keywords.push('MC')
        }

        if (companyId !== '') {
          keywords.push('Buro')
        }

        if (techCompanyId !== '') {
          keywords.push('Tech')
        }

        if (networkType !== '') {
          keywords.push('Network')
        }

        if (site !== '') {
          keywords.push('Site')
        }

        if (accountNo !== '') {
          keywords.push('Acc')
        }

        if (imei !== '') {
          keywords.push('IMEI')
        }

        if (serialNo !== '') {
          keywords.push('S/N')
        }

        if (mobileNo !== '') {
          keywords.push('Mobile')
        }

        if (ipAddress !== '') {
          keywords.push('IP')
        }

        if (simNo !== '') {
          keywords.push('Sim')
        }

        if (!!startDate || !!endDate) {
          keywords.push('Date')
        }

        return keywords
      },
      setTempState: (tempState: IManagementUnitsFilterProps) => {
        const {
          // open,
          status,
          mCentreId,
          companyId,
          techCompanyId,
          networkType,
          site,
          accountNo,
          imei,
          serialNo,
          mobileNo,
          ipAddress,
          simNo,
          unprocessedCancellations,
          hardwareToBeInvoiced,
          pinnedUnits,
          startDate,
          endDate,
          hasNoActivation,
          orderBy,
          savedState,
          skip,
          take
        } = get()

        set({
          ...tempState,
          savedState: {
            // open,
            status,
            mCentreId,
            companyId,
            techCompanyId,
            networkType,
            site,
            accountNo,
            imei,
            serialNo,
            mobileNo,
            ipAddress,
            simNo,
            unprocessedCancellations,
            hardwareToBeInvoiced,
            pinnedUnits,
            startDate,
            endDate,
            hasNoActivation,
            orderBy,
            savedState,
            skip,
            take
          }
        })
      },
      clear: () => {
        const { savedState } = get()

        if (savedState) {
          set(() => ({
            ...savedState
          }))
        } else {
          set(() => ({
            ...DEFAULT_MANAGEMENT_UNITS_FILTER_STORE_PROPS
          }))
        }
      }
    }),
    {
      name: 'management-units-filter-store'
    }
  )
)
